import { useEffect } from 'react';
import { refreshAccessToken } from '@/lib/api/refresh';
import { getAccessToken, onAccessTokenChange, setAccessToken } from '@/lib/api/token';
import { desktopBridge } from '../bridge';
import {
  isDesktopCallWindow,
  publishShellToken,
  publishShellTokenState,
} from '../callWindow';
import { useDesktopEvent } from './useDesktopEvent';

/**
 * THE TOKEN HANDOFF BETWEEN THE WORKSPACE WINDOW AND A CALL WINDOW.
 *
 * Both windows share one Electron session and therefore one `cxt_rt` refresh
 * cookie, which is single-use. If the call window ever rotates it, the API
 * revokes the workspace's session — and if the two race, it reads the loser as
 * token reuse and burns the whole family, dropping BOTH windows on the sign-in
 * page. That is not hypothetical; it is what shipped and was reverted.
 *
 * So there is exactly one rotator, and it is the workspace window:
 *
 *   WORKSPACE   publishes its token to the shell on every change, and rotates
 *               on demand when a call window asks for one.
 *   CALL WINDOW takes what it is given and never touches `/auth/refresh`
 *               (`lib/api/refresh.ts` refuses on its behalf).
 *
 * PUBLISHING IS NOT OPTIONAL AND NOT LAZY. The API tombstones a rotated session
 * in Redis and checks it on every bearer request, so the moment the workspace
 * rotates for its own reasons the call window's held token is dead even though
 * its `exp` is minutes away. A pull-only handoff would leave the call window
 * holding a revoked token with nothing to tell it. This pushes on every change.
 *
 * AND IT PUBLISHES THREE THINGS, NOT TWO — the fix for a mid-call sign-out that
 * a Ctrl+R was enough to cause. `null` had one meaning on this wire, "signed
 * out", and it was pushed to every call window as one. But the access token is
 * memory-only: EVERY fresh workspace document has none, and this effect runs
 * before `AuthProvider` has even issued its cold-start rotation, because
 * `DesktopRuntime` is a child of it. So reloading the workspace — Ctrl+R,
 * Ctrl+Shift+R, Help → Reload Workspace, Retry on the offline page — signed
 * every live call window out in the middle of its call.
 *
 * The rule that resolves it: only a TRANSITION to null is a sign-out, and the
 * token store's subscription is the only thing that can observe one (it fires
 * on CHANGE, and null cannot change to null). A READ of null is `unknown`, and
 * the broker does nothing with it at all.
 *
 * A no-op in a browser: `desktopBridge()` is null and neither branch runs.
 */
export function useDesktopAuthBridge(): void {
  /* ---- Workspace window: the sole rotator ------------------------------- */
  useEffect(() => {
    const bridge = desktopBridge();
    if (bridge === null || bridge.isCallWindow === true) return undefined;
    /* Seed the broker with what is already held: a call window may open before
       the next rotation, and there may not be one for fifteen minutes. A READ,
       so nothing is a sign-out here — on a fresh document there is no token to
       report yet and saying so must cost a live call window nothing. */
    publishShellTokenState(getAccessToken());
    /* A CHANGE, so null here really is the session ending. */
    return onAccessTokenChange((token) => publishShellToken(token));
  }, []);

  /**
   * A call window needs a token and the shell has nothing fresh.
   *
   * `refreshAccessToken` is single-flight WITHIN this document, so several call
   * windows asking at once collapse into one rotation — which is precisely the
   * guarantee that is lost the moment a second document rotates. Publishing is
   * handled by the subscription above; this only has to make it happen.
   */
  useDesktopEvent('auth-token-request', () => {
    /* Belt and braces. The shell only ever addresses this to the workspace
       window, and a call window that rotated here would ask the broker, which
       would ask the workspace, which is the loop this whole file avoids. */
    if (isDesktopCallWindow()) return;
    /* Published explicitly as well as through the subscription above, because
       `setAccessToken` is a no-op when the value did not change — a rotation
       that landed on the token already held would otherwise notify nobody and
       leave the asking window waiting for the broker's timeout.

       WHAT IS PUBLISHED IS WHAT WE NOW HOLD, not what `rotate()` returned. The
       two differ in exactly the case that matters: a rotation that never left
       the machine returns null and deliberately KEEPS the existing token
       ("dropping the session on a flaky network signs people out for walking
       into a lift"). Publishing that null as a sign-out would have signed the
       call window out for the blip this app refuses to sign the workspace out
       for. A real sign-out clears the store, and the subscription above says so
       on its own. */
    void refreshAccessToken()
      .then(() => publishShellTokenState(getAccessToken()))
      .catch(() => undefined);
  });

  /* ---- Call window: take what you are given ----------------------------- */
  useDesktopEvent('auth-token', (event) => {
    /* Including `null`, which is the workspace signing out. The auth context
       watches the token store and drops this window to the sign-in page with
       it, which is the correct end for a call window whose session is gone. */
    setAccessToken(event.token);
  });
}
